import { AES } from 'crypto-es/lib/aes.js';
import { Utf8 } from 'crypto-es/lib/core';

const SECRET_KEY = process.env.EXPO_PUBLIC_ENCRYPTION_SECRET!;
const DEFAULT_TTL_SECONDS = 120; // 2 minutes

type EncryptedPayload = {
  data: any;
  exp: number;
};

const getKey = (): string => {
  if (!SECRET_KEY) throw new Error('Missing EXPO_PUBLIC_ENCRYPTION_SECRET');
  return SECRET_KEY;
};

export const encryptObject = async (obj: Record<string, any>, options?: { ttlSeconds?: number }): Promise<string> => {
  const ttl = options?.ttlSeconds ?? DEFAULT_TTL_SECONDS;
  const exp = Math.floor(Date.now() / 1000) + ttl;
  const wrapped: EncryptedPayload = { data: obj, exp };
  const json = JSON.stringify(wrapped);
  const key = getKey();
  const encryptedBase64 = AES.encrypt(json, key).toString();
  return toUrlSafeBase64(encryptedBase64);
};

export const decryptObject = async <T>(encrypted: string): Promise<T> => {
  const key = getKey();

  try {
    const encryptedBase64 = fromUrlSafeBase64(encrypted);
    const bytes = AES.decrypt(encryptedBase64, key);
    const decrypted = bytes.toString(Utf8);

    if (!decrypted) {
      console.error('Decryption returned empty result');
      throw new Error('Decryption returned empty result');
    }

    const parsed = JSON.parse(decrypted);

    const now = Math.floor(Date.now() / 1000);
    if (parsed.exp < now) {
      throw new Error('Encrypted data has expired');
    }

    return parsed.data;
  } catch (error) {
    console.error('Decryption failed details:', error);
    throw new Error('Decryption failed. Possibly corrupted or expired.');
  }
};

const toUrlSafeBase64 = (base64: string): string => {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromUrlSafeBase64 = (safe: string): string => {
  const padded = safe.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (padded.length % 4)) % 4;
  return padded + '='.repeat(padLength);
};
